'use client'

import { useSiteConfig } from '@/feature/config/hooks/useSiteConfig.client'
import { handleErrors } from '@/lib/graphql/apiErrors'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { useNextIsClientHydrated } from '@/lib/next/useNextIsClientHydrated'
import { SearchInput } from '@/ui/form/inputs/search/SearchInput.client'
import { ErrorCode } from '@gql-generated/graphql'
import { type GraphQLFormattedError } from 'graphql'
import { usePostalCodeValidation } from 'postal-code-checker'
import { useCallback, useMemo, useState } from 'react'

/**
 * Minimum number of available points of sale required to display the search input.
 * Below this threshold, filtering via search is considered unnecessary.
 */
export const MIN_POINTS_OF_SALE_FOR_SEARCH = 4

type PointOfSalePostalCodeSearchInputProps = {
  defaultValue?: string
  onChange: (value: string) => void
  isLoading?: boolean
  error?: { graphQLErrors?: readonly GraphQLFormattedError[] }
}

export function PointOfSalePostalCodeSearchInput({
  defaultValue,
  onChange,
  isLoading,
  error,
}: PointOfSalePostalCodeSearchInputProps) {
  const { t } = useTranslation(['pointOfSalePostalCodeSearchInput'])
  const { config, loading: isConfigLoading } = useSiteConfig()
  const { validatePostalCode } = usePostalCodeValidation()
  const [errorMessage, setErrorMessage] = useState<string | undefined>()
  const isHydrated = useNextIsClientHydrated()

  const displayErrorMessage = useMemo(() => {
    if (isLoading) return undefined

    if (errorMessage) return errorMessage

    let apolloResult: string | undefined

    handleErrors(error?.graphQLErrors, {
      onError: {
        [ErrorCode.PointOfSaleSearchAddressNotExistError]: () => {
          apolloResult = t('pointOfSalePostalCodeSearchInput:error.pattern')
        },
      },
    })

    return apolloResult
  }, [isLoading, errorMessage, error, t])

  const handleSearchAction = useCallback(
    (value: string) => {
      setErrorMessage(undefined)

      if (!value) {
        onChange('')

        return
      }

      const currentCountryIso2 = config?.site?.defaultCountryIso2

      if (!currentCountryIso2) return

      // We only execute the search if the code format matches the country rules
      if (validatePostalCode(currentCountryIso2, value) === true) {
        onChange(value)
      } else {
        setErrorMessage(t('pointOfSalePostalCodeSearchInput:error.pattern'))
      }
    },
    [config?.site?.defaultCountryIso2, onChange, validatePostalCode, t],
  )

  if (!isHydrated || isConfigLoading || !config?.site?.defaultCountryIso2) {
    return null
  }

  return (
    <SearchInput
      defaultValue={defaultValue}
      onSearchAction={handleSearchAction}
      placeholder={t('pointOfSalePostalCodeSearchInput:label')}
      withDebounce={500}
      isLoading={isLoading}
      errorMessage={displayErrorMessage}
    />
  )
}
